import { useEffect, useState } from "react";
import { Download, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * PWAInstallButton — surfaces an install affordance only when the browser fires
 * the `beforeinstallprompt` event (Android Chrome, Desktop Chrome, Edge, Brave).
 * On iOS Safari (which doesn't fire that event) it remains hidden — iOS users use
 * Share → "Add to Home Screen" natively, so showing a fake CTA would be misleading.
 */
export const PWAInstallButton = ({ variant = "chip" }) => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [installed, setInstalled] = useState(false);

    useEffect(() => {
        // Already running as installed PWA?
        if (typeof window === "undefined") return;
        const isStandalone =
            window.matchMedia?.("(display-mode: standalone)")?.matches ||
            window.navigator?.standalone === true;
        if (isStandalone) {
            setInstalled(true);
            return;
        }

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferredPrompt(null);
            toast.success("Studio CMS installed", { description: "Launch it from your home screen." });
        };

        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    if (installed) {
        if (variant !== "banner") {
            return null;
        }
        return (
            <span
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em]"
                style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)", background: "var(--pg-surface-2)" }}
                data-testid="pwa-installed-chip"
            >
                <CheckCircle2 className="h-3 w-3" style={{ color: "var(--pg-accent)" }} /> Installed
            </span>
        );
    }

    if (!deferredPrompt) return null;

    const onInstall = async () => {
        try {
            deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;
            if (choice?.outcome !== "accepted") {
                toast("Install dismissed", { description: "You can install Studio CMS anytime from the admin nav." });
            }
        } catch (_e) {
            // ignore
        } finally {
            setDeferredPrompt(null);
        }
    };

    if (variant === "banner") {
        return (
            <div
                className="flex items-center justify-between gap-3 rounded-2xl border px-4 py-3"
                style={{
                    borderColor: "color-mix(in srgb, var(--pg-accent) 35%, var(--pg-border))",
                    background: "color-mix(in srgb, var(--pg-accent) 8%, var(--pg-surface))",
                }}
                data-testid="pwa-install-banner"
            >
                <div className="min-w-0">
                    <div className="font-display text-sm" style={{ color: "var(--pg-fg)" }}>Install Studio CMS</div>
                    <div className="text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                        One tap launch from your home screen. Works on Android &amp; Desktop.
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onInstall}
                    className="pg-btn-primary pg-app-tap shrink-0"
                    data-testid="pwa-install-banner-cta"
                >
                    <Download className="h-3.5 w-3.5" /> Install
                </button>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={onInstall}
            className="pg-app-tap inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors hover:bg-[var(--pg-surface-2)]"
            style={{ borderColor: "var(--pg-border-strong)", color: "var(--pg-fg)", background: "var(--pg-surface)" }}
            data-testid="pwa-install-chip"
        >
            <Download className="h-3 w-3" /> Install App
        </button>
    );
};

export default PWAInstallButton;
